import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { createImageEngine, createPdfEngine, createTextEngine, DEFAULT_IMAGE_SETTINGS, DEFAULT_KEYMAP, DEFAULT_TEXT_SETTINGS, } from 'porejs';
import { useReaderSource } from './provider.js';
import { createSettingsPersistence } from './settings-store.js';
const RuntimeContext = createContext(null);
export function Reader({ bookId, initialSettings, onPositionChange, onEnd, settingsKey, className, style, children, ref, persistSettings = true, transitions, fontFaceCss, }) {
    const source = useReaderSource();
    const hostRef = useRef(null);
    const engineRef = useRef(null);
    const onPosRef = useRef(onPositionChange);
    onPosRef.current = onPositionChange;
    const onEndRef = useRef(onEnd);
    onEndRef.current = onEnd;
    const settingsKeyRef = useRef(settingsKey);
    settingsKeyRef.current = settingsKey;
    const transitionsRef = useRef(transitions);
    transitionsRef.current = transitions;
    const fontFaceCssRef = useRef(fontFaceCss);
    fontFaceCssRef.current = fontFaceCss;
    const persistence = useMemo(() => persistSettings === false
        ? { initial: () => ({}), save: () => { } }
        : persistSettings === true
            ? createSettingsPersistence()
            : persistSettings, [persistSettings]);
    const [kind, setKind] = useState(null);
    const [manifest, setManifest] = useState(null);
    const [location, setLocation] = useState(null);
    const [progress, setProgress] = useState(null);
    const [settings, setSettings] = useState(() => ({
        ...DEFAULT_IMAGE_SETTINGS,
        ...initialSettings,
    }));
    const [keymap, setKeymap] = useState(DEFAULT_KEYMAP);
    const [toc, setToc] = useState([]);
    const [footnote, setFootnote] = useState(null);
    const [endPage, setEndPage] = useState(null);
    const [chromeVisible, setChromeVisible] = useState(true);
    const [resumedFromPage, setResumedFromPage] = useState(null);
    const [loading, setLoading] = useState(() => new Set());
    const [chapters, setChapters] = useState([]);
    const [error, setError] = useState(null);
    const [selection, setSelection] = useState(null);
    const [highlights, setHighlights] = useState([]);
    const [ttsState, setTtsState] = useState(null);
    useEffect(() => {
        const host = hostRef.current;
        if (!host)
            return;
        let disposed = false;
        const offs = [];
        void (async () => {
            let manifest;
            try {
                manifest = await source.getManifest(bookId);
            }
            catch (err) {
                if (!disposed)
                    setError({ error: err, at: Date.now() });
                return;
            }
            if (disposed)
                return;
            setManifest(manifest);
            const isText = manifest.type === 'epub';
            const isPdf = manifest.type === 'pdf';
            // pdf uses the image settings shape (it's the image engine underneath)
            const rk = isText ? 'text' : 'image';
            setKind(rk);
            const seeded = { ...persistence.initial(settingsKeyRef.current ?? bookId, rk), ...initialSettings };
            setSettings({
                ...(isText ? DEFAULT_TEXT_SETTINGS : DEFAULT_IMAGE_SETTINGS),
                ...seeded,
            });
            const txOpt = transitionsRef.current ? { transitions: transitionsRef.current } : {};
            const engine = (isText
                ? createTextEngine({
                    container: host,
                    source,
                    bookId,
                    settings: seeded,
                    ...(fontFaceCssRef.current ? { fontFaceCss: fontFaceCssRef.current } : {}),
                    ...txOpt,
                })
                : isPdf
                    ? createPdfEngine({
                        container: host,
                        source,
                        bookId,
                        settings: seeded,
                        ...txOpt,
                    })
                    : createImageEngine({
                        container: host,
                        source,
                        bookId,
                        settings: seeded,
                        ...txOpt,
                    }));
            engineRef.current = engine;
            const refreshChapters = () => setChapters(engine.chapters?.() ?? []);
            offs.push(engine.on('reader:locationchange', (p) => {
                const loc = p;
                setLocation(loc);
                onPosRef.current?.(loc);
            }), engine.on('reader:ready', () => refreshChapters()), engine.on('reader:progress', (p) => setProgress(p)), engine.on('reader:resumed', (p) => {
                const q = p;
                setResumedFromPage(q.position ? (q.page ?? 1) : null);
            }), engine.on('reader:settingschange', (p) => {
                const q = p;
                setSettings(q.settings);
                if (q.keymap)
                    setKeymap(q.keymap);
                persistence.save(settingsKeyRef.current ?? bookId, rk, q.settings);
            }), engine.on('reader:toc', (p) => {
                setToc(p.toc);
                refreshChapters();
            }), engine.on('reader:loadingstate', (p) => {
                const q = p;
                const key = String(q.index ?? q.spine ?? 0);
                setLoading((prev) => {
                    const next = new Set(prev);
                    if (q.state === 'loading')
                        next.add(key);
                    else
                        next.delete(key);
                    return next.size === prev.size && [...next].every((k) => prev.has(k)) ? prev : next;
                });
            }), engine.on('reader:footnote', (p) => setFootnote(p)), engine.on('reader:endpage', (p) => {
                const q = p;
                setEndPage(q.visible ? { kind: q.kind, label: q.label, hasNext: q.hasNext } : null);
                if (q.visible)
                    onEndRef.current?.({ kind: q.kind, hasNext: q.hasNext });
            }), engine.on('reader:chrometoggle', (p) => setChromeVisible(p.visible)), engine.on('reader:error', (p) => {
                const q = p;
                setError({ ...(q.index !== undefined ? { index: q.index } : {}), error: q.error, at: Date.now() });
            }), engine.on('reader:selection', (p) => setSelection(p)), engine.on('reader:highlightschange', (p) => setHighlights(p.highlights)), engine.on('reader:ttsstate', (p) => setTtsState(p)));
            try {
                await engine.mount();
            }
            catch (err) {
                if (!disposed)
                    setError({ error: err, at: Date.now() });
            }
        })();
        return () => {
            disposed = true;
            for (const off of offs)
                off();
            engineRef.current?.destroy();
            engineRef.current = null;
            setManifest(null);
            setSelection(null);
            setTtsState(null);
        };
    }, [source, bookId]);
    const handle = useMemo(() => ({
        turn: (d) => engineRef.current?.turn(d),
        goto: (t) => engineRef.current?.goto(t),
        goToHref: (href) => engineRef.current?.goToHref?.(href),
        search: (q) => engineRef.current?.search?.(q) ?? Promise.resolve([]),
        gotoHit: (hit) => engineRef.current?.gotoHit?.(hit),
        getCfi: () => engineRef.current?.getCfi?.() ?? null,
        goToCfi: (cfi) => engineRef.current?.goToCfi?.(cfi),
        addHighlight: (opts) => engineRef.current?.addHighlight?.(opts) ?? null,
        removeHighlight: (id) => engineRef.current?.removeHighlight?.(id),
        updateHighlight: (id, patch) => engineRef.current?.updateHighlight?.(id, patch) ?? null,
        listHighlights: () => engineRef.current?.listHighlights?.() ?? [],
        ttsPlay: () => engineRef.current?.ttsPlay?.(),
        ttsPause: () => engineRef.current?.ttsPause?.(),
        ttsResume: () => engineRef.current?.ttsResume?.(),
        ttsStop: () => engineRef.current?.ttsStop?.(),
        ttsSetRate: (rate) => engineRef.current?.ttsSetRate?.(rate),
        ttsSetVoice: (voice) => engineRef.current?.ttsSetVoice?.(voice),
        ttsListVoices: () => engineRef.current?.ttsListVoices?.() ?? [],
        setSettings: (patch) => engineRef.current?.setSettings(patch),
        setKeymap: (patch) => engineRef.current?.setKeymap?.(patch),
        chapters: () => engineRef.current?.chapters?.() ?? [],
    }), []);
    const clearFootnote = useMemo(() => () => setFootnote(null), []);
    const clearError = useMemo(() => () => setError(null), []);
    useImperativeHandle(ref, () => handle, [handle]);
    const ctx = useMemo(() => ({
        bookId,
        source,
        kind,
        manifest,
        location,
        progress,
        settings,
        keymap,
        toc,
        footnote,
        clearFootnote,
        endPage,
        chromeVisible,
        resumedFromPage,
        loading: loading.size > 0,
        chapters,
        error,
        clearError,
        handle,
        selection,
        highlights,
        ttsState,
    }), [
        bookId,
        source,
        kind,
        manifest,
        location,
        progress,
        settings,
        keymap,
        toc,
        footnote,
        clearFootnote,
        endPage,
        chapters,
        chromeVisible,
        resumedFromPage,
        loading,
        error,
        clearError,
        handle,
        selection,
        highlights,
        ttsState,
    ]);
    return (_jsxs(RuntimeContext.Provider, { value: ctx, children: [children, _jsx("div", { ref: hostRef, className: className, style: { flex: 1, minHeight: 0, minWidth: 0, ...style } })] }));
}
function useRuntime() {
    const ctx = useContext(RuntimeContext);
    if (!ctx)
        throw new Error('Reader hooks must be used inside <Reader>');
    return ctx;
}
export function useReaderKind() {
    return useRuntime().kind;
}
/** The current book's manifest (title, type, page/spine metadata) — `null` until loaded. */
export function useReaderManifest() {
    return useRuntime().manifest;
}
export function useReaderLocation() {
    return useRuntime().location;
}
/** The shell's "Ch 4 of 12 · 18 min left" data. `null` until the first paint. */
export function useReaderProgress() {
    return useRuntime().progress;
}
export function useReaderSettings() {
    const { settings, handle } = useRuntime();
    return [settings, handle.setSettings];
}
export function useReaderKeymap() {
    const { keymap, handle } = useRuntime();
    return [keymap, handle.setKeymap];
}
export function useTableOfContents() {
    return useRuntime().toc;
}
export function useFootnote() {
    const { footnote, clearFootnote } = useRuntime();
    return [footnote, clearFootnote];
}
export function useEndPage() {
    return useRuntime().endPage;
}
export function useChromeVisible() {
    return useRuntime().chromeVisible;
}
/** `true` while any page / spine image is still loading. */
export function useReaderLoading() {
    return useRuntime().loading;
}
/** The book's chapters (`{ id, label, startPage, startPercent }`), empty until known. */
export function useReaderChapters() {
    return useRuntime().chapters;
}
/** The most recent `reader:error`, with a dismiss and a best-effort retry. */
export function useReaderError() {
    const { error, clearError, location, handle } = useRuntime();
    const retry = useCallback(() => {
        if (location)
            handle.goto(location.page);
        clearError();
    }, [location, handle, clearError]);
    return { error, dismiss: clearError, retry };
}
export function useReader() {
    return useRuntime().handle;
}
export function useResumedFromPage() {
    return useRuntime().resumedFromPage;
}
/** Debounced in-book search wired to the active engine (text only today). */
export function useReaderSearch(opts) {
    const { handle, kind } = useRuntime();
    const debounceMs = opts?.debounceMs ?? 200;
    const [query, setQuery] = useState('');
    const [hits, setHits] = useState([]);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [busy, setBusy] = useState(false);
    const runId = useRef(0);
    useEffect(() => {
        setQuery('');
        setHits([]);
        setActiveIndex(-1);
    }, [kind]);
    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) {
            setHits([]);
            setActiveIndex(-1);
            return;
        }
        const id = ++runId.current;
        setBusy(true);
        const t = setTimeout(() => {
            void handle.search(q).then((res) => {
                if (id !== runId.current)
                    return;
                setHits(res);
                setActiveIndex(-1);
                setBusy(false);
            });
        }, debounceMs);
        return () => clearTimeout(t);
    }, [query, handle, debounceMs]);
    const go = useCallback((index) => {
        const hit = hits[index];
        if (!hit)
            return;
        setActiveIndex(index);
        handle.gotoHit(hit);
    }, [hits, handle]);
    return {
        query,
        setQuery,
        hits,
        activeIndex,
        busy,
        go,
        next: () => go(activeIndex + 1 < hits.length ? activeIndex + 1 : 0),
        prev: () => go(activeIndex > 0 ? activeIndex - 1 : hits.length - 1),
        clear: () => setQuery(''),
    };
}
/** The book's saved highlights (empty on engines without one, e.g. image/PDF). */
export function useReaderHighlights() {
    return useRuntime().highlights;
}
/**
 * Named bookmarks — a per-book collection persisted through the source's
 * optional `loadBookmarks` / `saveBookmarks`. Uses the handle's `getCfi()` /
 * `goToCfi()` so a bookmark survives re-pagination on a reflowable book.
 */
export function useBookmarks() {
    const { bookId, source, handle, location } = useRuntime();
    const supported = typeof source.loadBookmarks === 'function' && typeof source.saveBookmarks === 'function';
    const [bookmarks, setBookmarks] = useState([]);
    useEffect(() => {
        if (!supported)
            return;
        let live = true;
        void source.loadBookmarks?.(bookId).then((b) => {
            if (live)
                setBookmarks(b);
        });
        return () => {
            live = false;
        };
    }, [supported, source, bookId]);
    const persist = useCallback((next) => {
        const sorted = [...next].sort((a, b) => a.page - b.page || a.createdAt - b.createdAt);
        setBookmarks(sorted);
        void source.saveBookmarks?.(bookId, sorted).catch(() => { });
    }, [source, bookId]);
    const here = bookmarks.find((b) => b.page === (location?.page ?? -1)) ?? null;
    const add = useCallback((label) => {
        if (!supported || !location)
            return;
        const cfi = handle.getCfi() ?? undefined;
        const bm = {
            id: `bm_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
            position: location.position,
            ...(cfi ? { cfi } : {}),
            page: location.page,
            percent: location.percent,
            label: label?.trim() || location.label,
            createdAt: Date.now(),
        };
        persist([...bookmarks.filter((b) => b.page !== location.page), bm]);
    }, [supported, location, handle, bookmarks, persist]);
    const remove = useCallback((id) => persist(bookmarks.filter((b) => b.id !== id)), [bookmarks, persist]);
    const rename = useCallback((id, label) => persist(bookmarks.map((b) => (b.id === id ? { ...b, label } : b))), [bookmarks, persist]);
    const goTo = useCallback((bm) => {
        // `position` is exact for the current pagination; `cfi` (kept for export /
        // cross-device) is the fallback if the position can't be honoured.
        if (bm.position)
            handle.goto(bm.position);
        else if (bm.cfi)
            handle.goToCfi(bm.cfi);
    }, [handle]);
    const toggle = useCallback(() => {
        if (here)
            remove(here.id);
        else
            add();
    }, [here, remove, add]);
    return { bookmarks, isBookmarkedHere: !!here, supported, add, remove, rename, goTo, toggle };
}
/** Selection-driven highlighting — pair with `selection` to position a floating toolbar. */
export function useReaderSelection() {
    const { selection, handle } = useRuntime();
    return {
        selection,
        highlight: (opts) => handle.addHighlight(opts),
        removeHighlight: (id) => handle.removeHighlight(id),
        updateHighlight: (id, patch) => handle.updateHighlight(id, patch),
    };
}
/** Text-to-speech (stretch goal, EPUB-only) — safe to use on any engine; it's just inert without one. */
export function useTts() {
    const { ttsState, handle } = useRuntime();
    return {
        state: ttsState,
        play: () => handle.ttsPlay(),
        pause: () => handle.ttsPause(),
        resume: () => handle.ttsResume(),
        stop: () => handle.ttsStop(),
        setRate: (rate) => handle.ttsSetRate(rate),
        setVoice: (voice) => handle.ttsSetVoice(voice),
        listVoices: () => handle.ttsListVoices(),
    };
}
//# sourceMappingURL=reader.js.map